import type {BaseApiErrorRecovery, Middleware, ResponseContext} from '@linx-security/api-specs';
import {ApiErrorType} from '@linx-security/api-specs';
import type {Logger} from 'pino';

import {ApiError, ApiResponse, isErrorType} from '../types';

import {baseLogger} from './log-middleware';
import {safeParseResponseBody} from './parse-response-body';

const CATEGORY = `api-error`;

const STATUS_TO_ERROR_TYPE: Partial<Record<number, ApiErrorType>> = {
  404: ApiErrorType.NotFound,
  401: ApiErrorType.Unauthorized,
  403: ApiErrorType.Forbidden,
};

/**
 * Middleware to handle API errors and capture them using Datadog for tracking.
 *
 * The middleware distinguishes between two types of errors:
 *
 * 1. Intended errors: These are errors the Backend (BE) is aware of and
 *    sends to the Frontend (FE) in a predetermined structure. They are
 *    captured as messages in Sentry.
 *
 * 2. Unintended errors: These are errors that the BE is not aware of.
 *    They are captured as exceptions in Sentry.
 *
 * In both cases, errors are cast to ApiError so that they can be uniformly
 * handled by the FE.
 *
 * @param {Logger} logger - The logger instance, defaulting to `baseLogger`.
 * @returns {Middleware} - The middleware object with `onError` and `post` hooks.
 */
export const getCastErrorMiddleware: (logger: Logger) => Middleware = (logger: Logger = baseLogger): Middleware => ({
  /**
   * Handles issues with the fetching process itself, such as network errors.
   * Always throws ApiError so callers get a consistent error type.
   */

  onError: async context => {
    logger.error(
      {err: context.error, url: context.url, method: context.init?.method, category: CATEGORY},
      `API fetch error - ${context.url}`,
    );
    throw new ApiError({
      message: `API fetch error`,
      method: (context.init?.method ?? ``) as string,
      status: 0,
      url: context.url,
      type: ApiErrorType.InternalServerError,
    });
  },

  /**
   * Post-fetch middleware to handle and cast errors based on their type.
   *
   * @param {ResponseContext} context - The fetch response context.
   * @returns {Promise<Response>} - Returns either the original or modified fetch Response object.
   */
  post: async (context: ResponseContext): Promise<Response> => {
    const {response, init} = context;
    if (response.status < 400) return response;

    const {status, statusText, url} = response;
    const {method} = init;
    // 403/404 are usually permission or stale-link outcomes, not failures worth paging on.
    const level = status === 403 || status === 404 ? `warn` : `error`;

    // TODO: remove this after (PRE-2427)[https://linear.app/linxsecurity/issue/PRE-2427/[bug]-error-response-isnt-returned-in-the-expected-structure] is done
    const textResponses = [`application/problem+json`, `application/json`, `text/plain; charset=utf-8`] as const;
    let body: null | string | ApiResponse = null;
    if (textResponses.includes(response.headers.get(`Content-Type`) as (typeof textResponses)[number])) {
      body = await safeParseResponseBody(logger, response);
    }

    const parsedBody = body !== null && typeof body === 'object' ? body : null;
    const errorType: ApiErrorType | undefined = parsedBody?.error?.type;
    const recovery: BaseApiErrorRecovery | undefined = parsedBody?.error?.recovery;

    // Intended error: backend returned a known structured error type.
    if (isErrorType(errorType)) {
      logger[level](
        {status, statusText, url: context.url, method, type: errorType, intended: true, category: CATEGORY},
        `API error - ${context.url}`,
      );
      throw new ApiError({message: statusText, method, status, url, type: errorType, recovery});
    }

    // Transient error: gateway/upstream unavailable.
    if (status === 502 || status === 503 || status === 504) {
      logger.warn(
        {status, statusText, url: context.url, method, category: CATEGORY},
        `Transient API error - ${context.url}`,
      );
      throw new ApiError({message: statusText, method, status, url, type: ApiErrorType.InternalServerError, recovery});
    }

    // Unintended error: unknown shape or unmapped status code.
    logger[level](
      {status, statusText, url: context.url, method, body, category: CATEGORY},
      `API error response - ${context.url}`,
    );

    throw new ApiError({
      message: statusText,
      method,
      status,
      url,
      type: STATUS_TO_ERROR_TYPE[status] ?? ApiErrorType.InternalServerError,
      recovery,
      body,
    });
  },
});
